import React, { useState } from 'react';
import {
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  LinearProgress,
  Typography,
  Box,
  Alert,
} from '@mui/material';
import { format, addWeeks, isBefore, isAfter } from 'date-fns';
import { useTranslation } from 'react-i18next';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import { getSummaryReport, getDetailsPerUserReport } from '../services/reportApi';
import { SummaryReport, DetailsPerUserReport, SummaryReportItem, DetailsPerUserReportItem } from '../types/NewReport';

interface BatchReportGeneratorProps {
  open: boolean;
  onClose: () => void;
  startDate: Date;
  endDate: Date;
  reportType: 'summary' | 'details';
  groupBy?: 'media' | 'user';
  mediaType?: string;
  userId?: number;
  timeTrackedOnly?: boolean;
  detailsMediaType?: string;
}

interface WeeklyData {
  weekStart: Date;
  weekEnd: Date;
  data: SummaryReport | DetailsPerUserReport;
}

const BatchReportGenerator: React.FC<BatchReportGeneratorProps> = ({
  open,
  onClose,
  startDate,
  endDate,
  reportType,
  groupBy = 'media',
  mediaType,
  userId,
  timeTrackedOnly,
  detailsMediaType,
}) => {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(false);
  const [progress, setProgress] = useState(0);
  const [error, setError] = useState<string | null>(null);
  const [currentWeek, setCurrentWeek] = useState<string>('');

  const generateWeeklyRanges = (start: Date, end: Date): Array<{ start: Date; end: Date }> => {
    const ranges: Array<{ start: Date; end: Date }> = [];
    let currentStart = new Date(start); // Use exact start date, not week boundary

    while (isBefore(currentStart, end) || currentStart.getTime() === end.getTime()) {
      // Calculate one week from current start, but don't exceed the end date
      const weekEnd = addWeeks(currentStart, 1);
      const actualEnd = isAfter(weekEnd, end) ? end : new Date(weekEnd.getTime() - 24 * 60 * 60 * 1000); // Subtract 1 day to avoid overlap

      ranges.push({
        start: new Date(currentStart),
        end: actualEnd,
      });

      currentStart = addWeeks(currentStart, 1);
    }

    return ranges;
  };

  const fetchWeeklyData = async (weekRanges: Array<{ start: Date; end: Date }>): Promise<WeeklyData[]> => {
    const weeklyData: WeeklyData[] = [];

    // Debug: Log all the date ranges that will be fetched
    console.log('🗓️ DEBUG: Weekly ranges that will be fetched:');
    weekRanges.forEach((range, index) => {
      console.log(`Week ${index + 1}: ${format(range.start, 'yyyy-MM-dd')} to ${format(range.end, 'yyyy-MM-dd')}`);
    });

    for (let i = 0; i < weekRanges.length; i++) {
      const range = weekRanges[i];
      setCurrentWeek(`${format(range.start, 'MMM dd')} - ${format(range.end, 'MMM dd')}`);
      setProgress((i / weekRanges.length) * 100);

      console.log(`📊 Fetching data for: ${format(range.start, 'yyyy-MM-dd')} to ${format(range.end, 'yyyy-MM-dd')}`);

      try {
        let data: SummaryReport | DetailsPerUserReport;

        if (reportType === 'summary') {
          data = await getSummaryReport(
            format(range.start, 'yyyy-MM-dd'),
            format(range.end, 'yyyy-MM-dd'),
            groupBy,
            mediaType,
            timeTrackedOnly
          );
        } else {
          if (!userId) throw new Error('User ID is required for details report');
          data = await getDetailsPerUserReport(
            format(range.start, 'yyyy-MM-dd'),
            format(range.end, 'yyyy-MM-dd'),
            userId,
            timeTrackedOnly,
            detailsMediaType
          );
        }

        // Normalize backend response: support both legacy (array) and new ({ rows, totals, meta }) shapes
        const normalizedData: SummaryReport | DetailsPerUserReport = Array.isArray(data as unknown)
          ? (data as SummaryReport | DetailsPerUserReport)
          : Array.isArray((data as unknown as { rows?: unknown }).rows)
            ? (data as unknown as { rows: SummaryReport | DetailsPerUserReport }).rows
            : ([] as SummaryReport | DetailsPerUserReport);

        weeklyData.push({
          weekStart: range.start,
          weekEnd: range.end,
          data: normalizedData,
        });
      } catch (error) {
        console.error(`Error fetching data for week ${format(range.start, 'yyyy-MM-dd')}:`, error);
        throw new Error(
          `Failed to fetch data for week ${format(range.start, 'MMM dd')} - ${format(range.end, 'MMM dd')}`
        );
      }
    }

    return weeklyData;
  };

  const combineData = (weeklyData: WeeklyData[]): SummaryReport | DetailsPerUserReport => {
    if (weeklyData.length === 0) return [];

    if (reportType === 'summary') {
      const dataMap = new Map<string, SummaryReportItem>();

      weeklyData.forEach(({ data }) => {
        const items = Array.isArray(data) ? (data as SummaryReport) : [];
        items.forEach((item) => {
          const key = groupBy === 'media' ? `${item.mediaType}-${item.mediaId}` : `user-${item.userId}`;

          const existing = dataMap.get(key);
          if (existing) {
            existing.totalSecondsListened = (existing.totalSecondsListened || 0) + (item.totalSecondsListened || 0);
            existing.pagesViewed = (existing.pagesViewed || 0) + (item.pagesViewed || 0);
            const hoursBilled = (item as unknown as { hoursBilled?: number }).hoursBilled ?? 0;
            const compensation = (item as unknown as { compensationEuro?: number }).compensationEuro ?? 0;
            const unitRate = (item as unknown as { unitRateEuro?: number }).unitRateEuro;
            (existing as unknown as { hoursBilled?: number }).hoursBilled =
              ((existing as unknown as { hoursBilled?: number }).hoursBilled ?? 0) + hoursBilled;
            (existing as unknown as { compensationEuro?: number }).compensationEuro =
              ((existing as unknown as { compensationEuro?: number }).compensationEuro ?? 0) + compensation;
            if (unitRate != null) {
              (existing as unknown as { unitRateEuro?: number }).unitRateEuro = unitRate;
            }
          } else {
            dataMap.set(key, { ...item });
          }
        });
      });

      return Array.from(dataMap.values());
    } else {
      // For details report, combine all data
      const dataMap = new Map<string, DetailsPerUserReportItem>();

      weeklyData.forEach(({ data }) => {
        const items = Array.isArray(data) ? (data as DetailsPerUserReport) : [];
        items.forEach((item) => {
          const key = `${item.mediaType}-${item.mediaId}`;

          const existing = dataMap.get(key);
          if (existing) {
            existing.totalSecondsListened = (existing.totalSecondsListened || 0) + (item.totalSecondsListened || 0);
            existing.pagesViewed = (existing.pagesViewed || 0) + (item.pagesViewed || 0);
            const hoursBilled = (item as unknown as { hoursBilled?: number }).hoursBilled ?? 0;
            const compensation = (item as unknown as { compensationEuro?: number }).compensationEuro ?? 0;
            (existing as unknown as { hoursBilled?: number }).hoursBilled =
              ((existing as unknown as { hoursBilled?: number }).hoursBilled ?? 0) + hoursBilled;
            (existing as unknown as { compensationEuro?: number }).compensationEuro =
              ((existing as unknown as { compensationEuro?: number }).compensationEuro ?? 0) + compensation;
          } else {
            dataMap.set(key, { ...item });
          }
        });
      });

      return Array.from(dataMap.values());
    }
  };

  const formatHours = (seconds: number | null): string => {
    if (!seconds || seconds === 0) return '0.0';
    return (seconds / 3600).toFixed(1);
  };

  const generatePDF = (combinedData: SummaryReport | DetailsPerUserReport) => {
    const doc = new jsPDF();

    // Add title
    doc.setFontSize(18);
    doc.text(t('reports.media_usage_report'), 20, 20);

    // Add subtitle with date range
    doc.setFontSize(12);
    doc.text(`${t('reports.period')}: ${format(startDate, 'dd.MM.yyyy')} - ${format(endDate, 'dd.MM.yyyy')}`, 20, 30);

    let yPosition = 35;
    yPosition += 10;

    if (reportType === 'summary') {
      const summaryData = (combinedData as SummaryReport).filter((i) => (i?.totalSecondsListened ?? 0) > 0);

      // Calculate totals
      const totalSeconds = summaryData.reduce((sum, item) => sum + (item.totalSecondsListened || 0), 0);
      const consumedCount = summaryData.filter((item) => (item.totalSecondsListened || 0) > 0).length;
      const totalCompensation = summaryData.reduce((sum, item) => {
        const comp = (item as unknown as { compensationEuro?: number | null }).compensationEuro;
        if (comp != null) return sum + comp;
        const hb = (item as unknown as { hoursBilled?: number | null }).hoursBilled ?? 0;
        const rate = (item as unknown as { unitRateEuro?: number | null }).unitRateEuro ?? 0;
        return sum + hb * rate;
      }, 0);

      // Add summary statistics
      doc.setFontSize(14);
      doc.text(t('reports.summary_statistics'), 20, yPosition);
      yPosition += 8;

      doc.setFontSize(11);
      doc.text(`${t('reports.total_listening_hours_label')}: ${formatHours(totalSeconds)}`, 20, yPosition);
      yPosition += 5;
      doc.text(`${t('reports.consumed_works')}: ${consumedCount}`, 20, yPosition);
      yPosition += 5;
      doc.text(`${t('reports.total_compensation')}: ${totalCompensation.toFixed(2)}`, 20, yPosition);
      yPosition += 15;

      if (groupBy === 'media') {
        autoTable(doc, {
          head: [
            [
              t('reports.col_media_name'),
              t('reports.col_isbn'),
              t('reports.col_hours_listened'),
              t('reports.col_compensation'),
            ],
          ],
          body: summaryData.map((item) => {
            const isbn = (item as unknown as { isbn?: string | null }).isbn ?? '-';
            const comp = (item as unknown as { compensationEuro?: number | null }).compensationEuro;
            return [
              'mediaName' in item ? item.mediaName || '' : '',
              isbn,
              formatHours(item.totalSecondsListened),
              comp != null ? comp.toFixed(2) : '—',
            ];
          }),
          foot: [
            [
              t('reports.total'),
              '',
              formatHours(totalSeconds),
              summaryData
                .reduce((sum, i) => sum + ((i as unknown as { compensationEuro?: number }).compensationEuro || 0), 0)
                .toFixed(2),
            ],
          ],
          startY: yPosition,
          styles: { fontSize: 10 },
          headStyles: { fillColor: [66, 139, 202] },
          footStyles: { fillColor: [255, 255, 255], textColor: [0, 0, 0], fontStyle: 'bold' },
        });
      } else {
        autoTable(doc, {
          head: [[t('reports.col_user_name'), t('reports.col_hours_listened')]],
          body: summaryData.map((item) => [
            'userName' in item ? item.userName || '' : '',
            formatHours(item.totalSecondsListened),
          ]),
          foot: [[t('reports.total'), formatHours(totalSeconds)]],
          startY: yPosition,
          styles: { fontSize: 10 },
          headStyles: { fillColor: [66, 139, 202] },
          footStyles: { fillColor: [255, 255, 255], textColor: [0, 0, 0], fontStyle: 'bold' },
        });
      }
    } else {
      const detailsData = (combinedData as DetailsPerUserReport).filter((i) => (i?.totalSecondsListened ?? 0) > 0);

      // Calculate totals
      const totalSeconds = detailsData.reduce((sum, item) => sum + (item.totalSecondsListened || 0), 0);
      const consumedCount = detailsData.filter((item) => (item.totalSecondsListened || 0) > 0).length;
      const totalCompensation = detailsData.reduce((sum, item) => {
        const comp = (item as unknown as { compensationEuro?: number | null }).compensationEuro;
        if (comp != null) return sum + comp;
        const hb = (item as unknown as { hoursBilled?: number | null }).hoursBilled ?? 0;
        const rate = (item as unknown as { unitRateEuro?: number | null }).unitRateEuro ?? 0;
        return sum + hb * rate;
      }, 0);

      // Add summary statistics
      doc.setFontSize(14);
      doc.text(t('reports.user_details_summary'), 20, yPosition);
      yPosition += 8;

      doc.setFontSize(11);
      doc.text(`${t('reports.total_listening_hours_label')}: ${formatHours(totalSeconds)}`, 20, yPosition);
      yPosition += 5;
      doc.text(`${t('reports.consumed_works')}: ${consumedCount}`, 20, yPosition);
      yPosition += 5;
      doc.text(`${t('reports.total_compensation')}: ${totalCompensation.toFixed(2)}`, 20, yPosition);
      yPosition += 15;

      autoTable(doc, {
        head: [
          [
            t('reports.col_media_name'),
            t('reports.col_isbn'),
            t('reports.col_hours_listened'),
            t('reports.col_compensation'),
          ],
        ],
        body: detailsData.map((item) => {
          const isbn = (item as unknown as { isbn?: string | null }).isbn ?? '-';
          const comp = (item as unknown as { compensationEuro?: number | null }).compensationEuro;
          return [item.mediaName, isbn, formatHours(item.totalSecondsListened), comp != null ? comp.toFixed(2) : '—'];
        }),
        foot: [
          [
            t('reports.total'),
            '',
            formatHours(totalSeconds),
            detailsData
              .reduce((sum, i) => sum + ((i as unknown as { compensationEuro?: number }).compensationEuro || 0), 0)
              .toFixed(2),
          ],
        ],
        startY: yPosition,
        styles: { fontSize: 10 },
        headStyles: { fillColor: [66, 139, 202] },
        footStyles: { fillColor: [255, 255, 255], textColor: [0, 0, 0], fontStyle: 'bold' },
      });
    }

    // Save the PDF
    const filename = `media-report-${format(startDate, 'yyyy-MM-dd')}-to-${format(endDate, 'yyyy-MM-dd')}.pdf`;
    doc.save(filename);
  };

  const handleGenerate = async () => {
    setLoading(true);
    setError(null);
    setProgress(0);

    try {
      // Generate weekly ranges
      const weekRanges = generateWeeklyRanges(startDate, endDate);

      // Fetch data for each week
      const weeklyData = await fetchWeeklyData(weekRanges);

      // Combine all weekly data
      setCurrentWeek(t('reports.combining_data'));
      setProgress(90);
      const combinedData = combineData(weeklyData);

      // Generate PDF
      setCurrentWeek(t('reports.generating_pdf'));
      setProgress(95);
      generatePDF(combinedData);

      setProgress(100);
      setCurrentWeek(t('reports.complete'));

      // Close dialog after a short delay
      setTimeout(() => {
        onClose();
      }, 1000);
    } catch (error) {
      console.error('Error generating batch report:', error);
      setError(error instanceof Error ? error.message : 'An error occurred while generating the report');
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    if (!loading) {
      onClose();
      setError(null);
      setProgress(0);
      setCurrentWeek('');
    }
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>{t('reports.generate_batch_report')}</DialogTitle>
      <DialogContent>
        <Box sx={{ py: 2 }}>
          <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
            {t('reports.batch_report_description')}
          </Typography>

          {loading && (
            <Box sx={{ mb: 2 }}>
              <Typography variant="body2" sx={{ mb: 1 }}>
                {currentWeek}
              </Typography>
              <LinearProgress variant="determinate" value={progress} />
              <Typography variant="caption" sx={{ mt: 1, display: 'block' }}>
                {t('reports.percent_complete', { percent: Math.round(progress) })}
              </Typography>
            </Box>
          )}

          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} disabled={loading}>
          {t('reports.cancel')}
        </Button>
        <Button onClick={handleGenerate} variant="contained" disabled={loading}>
          {loading ? t('reports.generating') : t('reports.generate_pdf')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default BatchReportGenerator;
